
import React, { useState } from 'react';
import { Construction } from 'lucide-react';

const AthenaFlowsheet: React.FC = () => {
  const [activeTab, setActiveTab] = useState('vitals');

  const SidebarItem = ({ id, label, checked }: { id: string, label: string, checked?: boolean }) => (
    <div 
      onClick={() => setActiveTab(id)}
      className={`flex items-center space-x-3 px-4 py-3 cursor-pointer border-b border-gray-100 transition-colors ${activeTab === id ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50'}`}
    >
      <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
        {checked && <i className="fa-solid fa-check text-white text-[10px]"></i>}
      </div>
      <span className={`text-[13px] ${activeTab === id ? 'font-bold' : 'text-gray-700'}`}>{label}</span>
    </div>
  );

  return (
    <div className="h-full bg-[#f1f1f5] flex flex-col font-sans relative">
      {/* Coming Soon Overlay */}
      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white px-8 py-6 rounded-2xl shadow-xl border border-slate-200 flex flex-col items-center">
          <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
            <Construction className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Coming Soon</h2>
          <p className="text-slate-500 text-center max-w-xs text-sm">
            We're currently fine-tuning this workflow. Check back later!
          </p>
        </div>
      </div>

      {/* Purple Patient Header */}
      <header className="bg-[#3e2d5c] text-white p-3 flex justify-between items-center h-24">
        <div className="flex items-center space-x-4">
          <div className="w-14 h-14 bg-gray-300 rounded overflow-hidden border-2 border-white/20">
            <img src="https://picsum.photos/seed/jake/100/100" alt="Patient" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Jake MEDLOCK</h1>
            <div className="flex items-center space-x-3 text-xs opacity-90">
              <span>69yo M</span>
              <span>06-24-1946</span>
              <span>#2446</span>
              <i className="fa-solid fa-magnifying-glass cursor-pointer"></i>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex flex-col">
            <span className="opacity-70 text-[10px] uppercase">Patient Status</span>
            <input type="text" defaultValue="" placeholder="status..." className="bg-transparent border-b border-white/30 text-white focus:outline-none text-[13px] placeholder:text-white/30" />
          </div>
          <div className="flex flex-col">
            <span className="opacity-70 text-[10px] uppercase">Patient Location</span>
            <input type="text" defaultValue="" placeholder="location..." className="bg-transparent border-b border-white/30 text-white focus:outline-none text-[13px] placeholder:text-white/30" />
          </div>
          <button className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-sm text-sm font-semibold transition-colors">
            Done with Intake
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="px-4 py-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Follow-up</div>
          <SidebarItem id="vitals" label="Vitals" checked />
          <SidebarItem id="allergies" label="Allergies" checked />
          <SidebarItem id="medications" label="Medications" checked />
          <SidebarItem id="vaccines" label="Vaccines" />
          <SidebarItem id="problems" label="Problems" />
          <SidebarItem id="family" label="Family History" />
          <SidebarItem id="social" label="Social History" />
          <SidebarItem id="surgical" label="Surgical History" />
          <SidebarItem id="medical" label="Past Medical History" />
          <SidebarItem id="screening" label="Screening" />
          <SidebarItem id="quality" label="Quality Measures (14)" />
          <SidebarItem id="hpi" label="History of Present Illness" />
          <SidebarItem id="ros" label="Review of Systems" />
        </div>

        {/* Content Area */}
        <div className="flex-1 p-8 overflow-y-auto bg-[#e5e5eb]">
          <div className="max-w-4xl mx-auto bg-white rounded shadow-sm min-h-[600px] flex flex-col">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl text-gray-600 capitalize">{activeTab}</h2>
              <button className="bg-[#8eb62a] hover:bg-[#7da124] text-white px-8 py-1.5 rounded text-sm font-semibold">Next</button>
            </div>

            <div className="p-8 flex-1">
              {activeTab === 'vitals' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <label className="col-span-1 text-sm font-medium text-gray-500">Ht</label>
                    <div className="col-span-11 flex items-center space-x-2">
                      <input type="text" className="w-16 border border-gray-300 p-1.5 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none" defaultValue="" />
                      <span className="text-xs text-gray-400 italic">ft</span>
                      <input type="text" className="w-16 border border-gray-300 p-1.5 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none" defaultValue="" />
                      <span className="text-xs text-gray-400 italic">in</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <label className="col-span-1 text-sm font-medium text-gray-500">Wt</label>
                    <div className="col-span-11 flex items-center space-x-4">
                      <input type="text" className="w-20 border border-gray-300 p-1.5 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none" defaultValue="" />
                      <input type="text" placeholder="unit (lbs/kg)" className="w-32 border border-gray-300 p-1.5 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none text-xs" defaultValue="" />
                      <label className="flex items-center space-x-1 text-xs text-gray-600 cursor-pointer">
                        <input type="checkbox" /> <span>Refused</span>
                      </label>
                      <i className="fa-solid fa-circle-plus text-blue-400 text-xl cursor-pointer"></i>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <label className="col-span-1 text-sm font-medium text-gray-500">BMI</label>
                    <div className="col-span-11">
                      <input type="text" className="w-20 border border-gray-300 p-1.5 rounded focus:outline-none" defaultValue="" />
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <label className="col-span-1 text-sm font-medium text-gray-500">BP</label>
                    <div className="col-span-11 flex items-center space-x-2">
                      <input type="text" placeholder="sys" className="w-16 border border-gray-300 p-1.5 rounded focus:outline-none" defaultValue="" />
                      <span className="text-gray-400">/</span>
                      <input type="text" placeholder="dia" className="w-16 border border-gray-300 p-1.5 rounded focus:outline-none" defaultValue="" />
                      <input type="text" placeholder="position" className="w-32 border border-gray-300 p-1.5 rounded focus:outline-none text-xs" defaultValue="" />
                      <input type="text" placeholder="location" className="w-32 border border-gray-300 p-1.5 rounded focus:outline-none text-xs" defaultValue="" />
                      <i className="fa-solid fa-circle-plus text-blue-400 text-xl cursor-pointer"></i>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <label className="col-span-1 text-sm font-medium text-gray-500">Pulse</label>
                    <div className="col-span-11 flex items-center space-x-2">
                      <input type="text" className="w-16 border border-gray-300 p-1.5 rounded focus:outline-none" defaultValue="" />
                      <input type="text" placeholder="unit" className="w-24 border border-gray-300 p-1.5 rounded focus:outline-none text-xs" defaultValue="" />
                      <i className="fa-solid fa-circle-plus text-blue-400 text-xl cursor-pointer"></i>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-start mt-10">
                    <div className="col-span-1 flex items-center text-gray-400 space-x-1 mt-1">
                      <i className="fa-regular fa-file-lines"></i>
                      <span className="text-[10px] font-bold uppercase">Note</span>
                    </div>
                    <div className="col-span-11">
                      <textarea 
                        className="w-full border border-gray-200 p-3 h-32 focus:outline-none focus:ring-1 focus:ring-blue-400 text-sm placeholder:italic"
                        placeholder="Enter exam notes here for the dictation test..."
                        defaultValue=""
                      ></textarea>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'allergies' && (
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 border border-gray-200 rounded">
                    <h4 className="text-sm font-bold text-gray-700 mb-3">Add Allergy</h4>
                    <div className="flex space-x-4">
                      <input type="text" placeholder="Search for allergen..." className="flex-1 border border-gray-300 p-2 text-sm focus:outline-none" defaultValue="" />
                      <input type="text" placeholder="Reaction" className="w-1/3 border border-gray-300 p-2 text-sm focus:outline-none" defaultValue="" />
                      <button className="bg-blue-600 text-white px-4 py-2 rounded text-sm">Add</button>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 border-b border-gray-200">
                        <tr>
                          <th className="p-3 text-left font-semibold text-gray-600">Allergen</th>
                          <th className="p-3 text-left font-semibold text-gray-600">Reaction</th>
                          <th className="p-3 text-left font-semibold text-gray-600">Severity</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="p-3 text-gray-400 italic">No allergies recorded...</td>
                          <td className="p-3"></td>
                          <td className="p-3"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-8">
                    <label className="text-xs font-bold text-gray-400 uppercase">Notes on Allergies</label>
                    <textarea 
                      placeholder="Add detailed allergy notes via dictation..."
                      className="w-full border border-gray-200 p-3 h-32 mt-2 focus:outline-none text-sm"
                      defaultValue=""
                    ></textarea>
                  </div>
                </div>
              )}

              {activeTab === 'medications' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center bg-blue-50 p-4 border border-blue-100 rounded">
                    <div className="flex items-center space-x-2 text-blue-800">
                      <i className="fa-solid fa-info-circle"></i>
                      <span className="text-sm font-medium">Add or verify medications</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="border border-gray-200 rounded p-4 flex flex-col space-y-3">
                      <input type="text" placeholder="Search for new medication..." className="w-full border border-gray-300 p-2 text-sm focus:outline-none" defaultValue="" />
                      <textarea placeholder="Medication SIG / Instructions..." className="w-full border border-gray-200 p-2 text-sm focus:outline-none h-20" defaultValue=""></textarea>
                    </div>
                  </div>

                  <div className="mt-8 pt-8 border-t border-gray-100">
                    <h4 className="text-sm font-bold text-gray-700 mb-4">Dictate Additional Medication Instructions</h4>
                    <textarea 
                      placeholder="Add special instructions or changes to med regimen..."
                      className="w-full border border-gray-200 p-3 h-40 focus:outline-none text-sm"
                      defaultValue=""
                    ></textarea>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer bar */}
      <footer className="bg-[#f1f1f5] border-t border-gray-300 p-1 px-4 flex justify-between items-center text-[11px] text-gray-500">
        <div className="flex items-center space-x-4">
          <span>kwu1</span>
          <span>BLUE HILL</span>
          <div className="flex space-x-2">
            <span className="bg-gray-200 px-2 rounded">0</span>
            <span className="bg-orange-100 px-2 rounded text-orange-600">0</span>
            <span>0 tasks</span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <i className="fa-regular fa-comment"></i>
          <i className="fa-solid fa-circle-question"></i>
        </div>
      </footer>
    </div>
  );
};

export default AthenaFlowsheet;
